import { Button } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import type { ReactNode } from 'react';
import { Link } from 'react-router';

/**
 * Shared sidebar chrome. Colors and layout classes (`.side-panel`,
 * `.menu-size-button`, `.side-panel .ant-menu`) live in each app stylesheet
 * and consume that app’s `--color-*` tokens. Do not import generate-tax
 * `index.scss` (or Rows `theme.css`) into shared-lib.
 */

export type AppSidebarSize = 'wide' | 'narrow';

export const APP_SIDEBAR_WIDTH_WIDE_PX = 212;
export const APP_SIDEBAR_WIDTH_NARROW_PX = 88;

export type AppSidePanelProps = {
  menuSize: AppSidebarSize;
  logoSrc: string;
  logoHref: string;
  logoAriaLabel: string;
  /** Wide-menu logo width in px. Narrow uses a smaller default. */
  logoWidthWide?: number;
  logoWidthNarrow?: number;
  canToggleMenu?: boolean;
  isRtl?: boolean;
  onToggleMenu?: () => void;
  hideMenuLabel?: string;
  showMenuLabel?: string;
  /** Nav list (usually `<AppSideMenu />`). */
  children: ReactNode;
  /** Account chip / logout (usually `<AppUserMenu />`). */
  footer?: ReactNode;
  /** Optional dark-theme logo; swapped via `.app-logo--light` / `--dark`. */
  logoSrcDark?: string;
};

export const AppSidePanel = ({
  menuSize,
  logoSrc,
  logoHref,
  logoAriaLabel,
  logoWidthWide = 142,
  logoWidthNarrow = 53,
  canToggleMenu = true,
  isRtl = false,
  onToggleMenu,
  hideMenuLabel = 'Hide menu',
  showMenuLabel = 'Show menu',
  children,
  footer,
  logoSrcDark,
}: AppSidePanelProps) => {
  const isWide = menuSize === 'wide';
  const menuWidth = isWide
    ? APP_SIDEBAR_WIDTH_WIDE_PX
    : APP_SIDEBAR_WIDTH_NARROW_PX;
  const logoWidth = isWide ? logoWidthWide : logoWidthNarrow;
  const pointTowardOuterEdge = isRtl ? !isWide : isWide;

  return (
    <div
      className={`side-panel ${isWide ? 'side-panel--wide' : 'side-panel--narrow'}`}
      style={{ width: menuWidth }}
    >
      <div className="side-panel-row">
        <div className="side-panel-top">
          <Link to={logoHref} aria-label={logoAriaLabel}>
            {logoSrcDark ? (
              <>
                <img
                  className="app-logo--light"
                  src={logoSrc}
                  width={logoWidth}
                  alt=""
                />
                <img
                  className="app-logo--dark"
                  src={logoSrcDark}
                  width={logoWidth}
                  alt=""
                />
              </>
            ) : (
              <img src={logoSrc} width={logoWidth} alt="" />
            )}
          </Link>
          {children}
        </div>
      </div>
      {canToggleMenu && onToggleMenu && (
        <Button
          className="menu-size-button"
          icon={
            pointTowardOuterEdge ? (
              <LeftOutlined style={{ width: 10 }} />
            ) : (
              <RightOutlined style={{ width: 10 }} />
            )
          }
          style={{
            position: 'absolute',
            top: 74,
            ...(isRtl
              ? { left: -16, right: 'auto' }
              : { right: -16, left: 'auto' }),
          }}
          onClick={onToggleMenu}
          aria-label={isWide ? hideMenuLabel : showMenuLabel}
        />
      )}
      {footer}
    </div>
  );
};
